/**
 * Schulen des Trägers. Alle Träger-User sehen ihre Schulen und können sie öffnen; der
 * Haupt-Trägeradmin legt zusätzlich neue Schulen an, trennt Schulen vom Träger und nimmt
 * Anfragen zum Verbinden an oder lehnt sie ab.
 */
import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { showToast } from "../utils/toast";
import { openCarrierSchool } from "../utils/carrier-view";
import type { CarrierInfo, CarrierRequest, CarrierSchool } from "../types";
import { esc, formatDate, schoolBadges, setRequestCount } from "./traeger-shared";

let info: CarrierInfo | null = null;
let schools: CarrierSchool[] = [];
let requests: CarrierRequest[] = [];

export async function render(el: HTMLElement) {
  [info, schools] = await Promise.all([api.carrier.get(), api.carrier.schools()]);
  requests = info.isFounder ? await api.carrier.requests() : [];
  setRequestCount(requests.length);
  const founder = info.isFounder;
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Schulen</h1>
        <div id="tr-limit-count" style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem"></div>
      </div>
      ${founder ? `<button class="btn btn-primary" id="tr-add-school">+ Neue Schule anlegen</button>` : ""}
    </div>
    <div id="tr-limit"></div>
    <div id="tr-requests"></div>
    <div class="card table-wrap">
      <table>
        <thead><tr><th>Schule</th><th>Ort</th><th></th><th style="width:220px">Aktionen</th></tr></thead>
        <tbody id="tr-school-tbody"></tbody>
      </table>
    </div>
    <p class="text-muted" style="font-size:.8125rem;margin-top:1rem">
      Weitere Schulen stellen mit dem Code des Schulträgers (siehe Einstellungen) eine Anfrage. Die Schule gibt ihn unter Einstellungen, Allgemein ein.
      ${founder ? "Verbunden ist sie erst, wenn du die Anfrage hier annimmst." : "Verbunden ist sie erst, wenn der Haupt-Trägeradmin die Anfrage annimmt."}
    </p>`;
  renderTable(el);
  renderRequests(el);
  renderLimit(el);

  el.querySelector("#tr-add-school")?.addEventListener("click", () => openCreateSchool(el));
  el.querySelector("#tr-school-tbody")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;
    const openBtn = target.closest<HTMLButtonElement>("[data-open]");
    if (openBtn) { openCarrierSchool(Number(openBtn.dataset.open), info!.homeSchoolId); return; }
    const removeBtn = target.closest<HTMLButtonElement>("[data-remove]");
    if (removeBtn) await removeSchool(el, Number(removeBtn.dataset.remove));
  });
  el.querySelector("#tr-requests")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;
    const acceptBtn = target.closest<HTMLButtonElement>("[data-accept]");
    if (acceptBtn) { await decideRequest(el, Number(acceptBtn.dataset.accept), true); return; }
    const rejectBtn = target.closest<HTMLButtonElement>("[data-reject]");
    if (rejectBtn) await decideRequest(el, Number(rejectBtn.dataset.reject), false);
  });
}

/** Das Schullimit setzt der Plattform-Admin; gezählt werden alle Schulen samt Stammschule. */
function limitReached(): boolean {
  return info!.maxSchools !== null && info!.schoolCount >= info!.maxSchools;
}

const LIMIT_HINT = "Dein Schulträger hat die maximale Anzahl Schulen erreicht. Wende dich an den Support (support@meduvio.de), um das Limit zu erhöhen.";

/** Zähler „3 von 5 Schulen“ im Kopf, Hinweis und gesperrtes Anlegen bei erreichtem Limit. */
function renderLimit(el: HTMLElement) {
  const max = info!.maxSchools;
  el.querySelector("#tr-limit-count")!.textContent = max === null ? "" : `${info!.schoolCount} von ${max === 1 ? "1 Schule" : `${max} Schulen`}`;
  const reached = limitReached();
  el.querySelector("#tr-limit")!.innerHTML = reached && info!.isFounder
    ? `<div class="alert alert-warning" style="margin-bottom:1.25rem">${esc(LIMIT_HINT)}</div>`
    : "";
  const addBtn = el.querySelector<HTMLButtonElement>("#tr-add-school");
  if (addBtn) {
    addBtn.disabled = reached;
    addBtn.title = reached ? LIMIT_HINT : "";
  }
}

/** Nach Annehmen, Anlegen oder Trennen: Schulen, Anfragen und Zähler neu laden. */
async function reloadAll(el: HTMLElement) {
  [info, schools] = await Promise.all([api.carrier.get(), api.carrier.schools()]);
  requests = info.isFounder ? await api.carrier.requests() : [];
  setRequestCount(requests.length);
  renderTable(el);
  renderRequests(el);
  renderLimit(el);
}

/** Karte mit den offenen Anfragen, nur sichtbar, solange es welche gibt. */
function renderRequests(el: HTMLElement) {
  const box = el.querySelector("#tr-requests")!;
  if (requests.length === 0) { box.innerHTML = ""; return; }
  box.innerHTML = `
    <div class="card" style="margin-bottom:1.25rem;border-color:#ddd6fe">
      <div style="padding:1rem 1.25rem .5rem">
        <h2 style="font-size:1rem;font-weight:600;margin:0">Anfragen zum Verbinden</h2>
        <p class="text-muted" style="font-size:.8125rem;margin:.25rem 0 0">
          Diese Schulen möchten sich mit dem Schulträger verbinden. Nimm nur Schulen an, die du kennst:
          Du und freigegebene Träger-User arbeiten dort anschließend als Schul-Admin.
        </p>
      </div>
      <div class="table-wrap"><table>
        <thead><tr><th>Schule</th><th>Angefragt von</th><th>Am</th><th style="width:220px">Aktionen</th></tr></thead>
        <tbody>${requests.map(r => `
          <tr>
            <td><div style="font-weight:500">${esc(r.school.name)}</div><div class="text-muted" style="font-size:.8125rem">${esc(r.school.ort || "")}</div></td>
            <td>${r.requestedBy
              ? `${esc(r.requestedBy.name)}<div class="text-muted" style="font-size:.8125rem">${esc(r.requestedBy.email)}</div>`
              : `<span class="text-muted">unbekannt</span>`}</td>
            <td style="white-space:nowrap">${formatDate(r.createdAt)}</td>
            <td style="white-space:nowrap">
              <button class="btn btn-sm btn-primary" data-accept="${r.id}"${limitReached() ? ` disabled title="${esc(LIMIT_HINT)}"` : ""}>Annehmen</button>
              <button class="btn btn-sm" data-reject="${r.id}">Ablehnen</button>
            </td>
          </tr>`).join("")}
        </tbody>
      </table></div>
    </div>`;
}

async function decideRequest(el: HTMLElement, id: number, accept: boolean) {
  const r = requests.find(x => x.id === id);
  if (!r) return;
  const ok = await confirmDialog(accept
    ? {
        title: "Anfrage annehmen",
        message: `„${r.school.name}“ wird mit dem Schulträger verbunden. Du und freigegebene Träger-User können dort anschließend als Schul-Admin arbeiten.`,
        confirmLabel: "Annehmen",
      }
    : {
        title: "Anfrage ablehnen",
        message: `„${r.school.name}“ wird nicht verbunden. Die Schule sieht, dass du die Anfrage abgelehnt hast.`,
        confirmLabel: "Ablehnen",
        danger: true,
      });
  if (!ok) return;
  try {
    if (accept) await api.carrier.acceptRequest(id);
    else await api.carrier.rejectRequest(id);
    await reloadAll(el);
    showToast(accept ? "Schule verbunden" : "Anfrage abgelehnt");
  } catch (err: any) { alert("Fehler: " + err.message); }
}

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("#tr-school-tbody")!;
  if (schools.length === 0) {
    tbody.innerHTML = `<tr><td colspan="4" class="empty-state">Keine Schulen</td></tr>`;
    return;
  }
  tbody.innerHTML = schools.map(s => `
    <tr>
      <td style="font-weight:500">${esc(s.name)}</td>
      <td class="text-muted">${esc(s.ort || "")}</td>
      <td>${schoolBadges(s)}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm btn-primary" data-open="${s.id}">Öffnen</button>
        ${info!.isFounder && !s.isFoundingSchool ? `<button class="btn btn-sm" data-remove="${s.id}">Vom Träger trennen</button>` : ""}
      </td>
    </tr>`).join("");
}

async function removeSchool(el: HTMLElement, id: number) {
  const s = schools.find(x => x.id === id);
  if (!s) return;
  const ok = await confirmDialog({
    title: "Schule vom Schulträger trennen",
    message: `„${s.name}“ wird vom Schulträger getrennt. Die Daten der Schule bleiben erhalten.\n\nTräger-User dieser Schule verlieren ihren Zugang zum Schulträger, und niemand kann die Schule mehr über den Schulträger öffnen.`,
    confirmLabel: "Trennen",
    danger: true,
  });
  if (!ok) return;
  try {
    await api.carrier.removeSchool(id);
    await reloadAll(el);
    showToast("Schule getrennt");
  } catch (err: any) { alert("Fehler: " + err.message); }
}

function openCreateSchool(el: HTMLElement) {
  openModal("Neue Schule anlegen", `
    <div class="form-grid">
      <div class="form-group col-span-2"><label>Name der Schule *</label><input id="ns-name" /></div>
      <div class="form-group col-span-2"><label>Straße und Hausnummer *</label><input id="ns-address" /></div>
      <div class="form-group"><label>PLZ *</label><input id="ns-plz" /></div>
      <div class="form-group"><label>Ort *</label><input id="ns-ort" /></div>
      <div class="form-group col-span-2"><label>E-Mail der Schule *</label><input id="ns-email" type="email" /></div>
      <div class="form-group col-span-2" style="font-size:.875rem;color:var(--gray-600)">
        Die Schule ist sofort mit dem Schulträger verbunden und startet mit 14 Tagen Testphase für alle Module.
        Du kannst sie danach öffnen und dort Benutzer anlegen. Abonnements bucht jede Schule für sich.
      </div>
      <label class="col-span-2" style="display:flex;gap:.5rem;align-items:flex-start;font-size:.875rem">
        <input type="checkbox" id="ns-agb" style="margin-top:.2rem" />
        <span>Ich akzeptiere im Namen der Schule die <a href="#/agb" target="_blank" rel="noopener">AGB</a>.</span>
      </label>
      <label class="col-span-2" style="display:flex;gap:.5rem;align-items:flex-start;font-size:.875rem">
        <input type="checkbox" id="ns-avv" style="margin-top:.2rem" />
        <span>Ich stimme im Namen der Schule der <a href="#/avv" target="_blank" rel="noopener">Auftragsverarbeitungsvereinbarung</a> zu.</span>
      </label>
    </div>
    <div id="ns-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem;margin-top:.5rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="ns-cancel">Abbrechen</button><button class="btn btn-primary" id="ns-save">Schule anlegen</button></div>`, { wide: true });

  const mb = modalBody();
  const val = (id: string) => (mb.querySelector(`#${id}`) as HTMLInputElement).value.trim();
  mb.querySelector("#ns-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#ns-save")!.addEventListener("click", async () => {
    const errorEl = mb.querySelector("#ns-error") as HTMLElement;
    errorEl.textContent = "";
    const d = {
      name: val("ns-name"), address: val("ns-address"), plz: val("ns-plz"), ort: val("ns-ort"), email: val("ns-email"),
      agbAccepted: (mb.querySelector("#ns-agb") as HTMLInputElement).checked,
      avvAccepted: (mb.querySelector("#ns-avv") as HTMLInputElement).checked,
    };
    if (!d.name || !d.address || !d.plz || !d.ort || !d.email) { errorEl.textContent = "Bitte alle Pflichtfelder ausfüllen."; return; }
    if (!d.agbAccepted || !d.avvAccepted) { errorEl.textContent = "Bitte AGB und Auftragsverarbeitungsvereinbarung bestätigen."; return; }
    const btn = mb.querySelector("#ns-save") as HTMLButtonElement;
    btn.disabled = true;
    try {
      await api.carrier.createSchool(d);
      closeModal();
      await reloadAll(el);
      showToast("Schule angelegt");
    } catch (err: any) {
      errorEl.textContent = err.message;
      btn.disabled = false;
    }
  });
}
